// @tennis-matches/services/masterdata/club-person-name-match
// Vor-/Nachname-Deduplizierung innerhalb eines Tenants (ohne Trainer-Spiegel).

import { getSupabaseClient } from "../supabase/index.js";
import { CLUB_PERSON_CONTACT_SOURCE_TRAINER } from "./contact-source.js";

function __asStr(v) {
  return String(v ?? "").trim();
}

/**
 * @param {unknown} part
 */
export function normalizeClubPersonNamePart(part) {
  return __asStr(part).toLowerCase().replace(/\s+/g, " ");
}

/**
 * @param {unknown} firstName
 * @param {unknown} lastName
 */
export function clubPersonNameKey(firstName, lastName) {
  const fn = normalizeClubPersonNamePart(firstName);
  const ln = normalizeClubPersonNamePart(lastName);
  if (!fn || !ln) return "";
  return `${fn}\u0000${ln}`;
}

/**
 * @param {unknown} person
 * @param {unknown} firstName
 * @param {unknown} lastName
 */
export function clubPersonMatchesNormalizedName(person, firstName, lastName) {
  const key = clubPersonNameKey(firstName, lastName);
  if (!key) return false;
  return clubPersonNameKey(person?.first_name, person?.last_name) === key;
}

/**
 * @param {unknown} person
 */
export function isTrainerMirrorPersonRow(person) {
  return __asStr(person?.contact_source) === CLUB_PERSON_CONTACT_SOURCE_TRAINER;
}

/**
 * @param {unknown[]} rows
 * @param {unknown} firstName
 * @param {unknown} lastName
 * @param {{ excludeIds?: string[] }} [options]
 */
export function filterClubPeopleByNormalizedName(
  rows,
  firstName,
  lastName,
  options = {},
) {
  const exclude = new Set(
    (Array.isArray(options.excludeIds) ? options.excludeIds : [])
      .map(__asStr)
      .filter(Boolean),
  );
  const key = clubPersonNameKey(firstName, lastName);
  if (!key) return [];

  return (Array.isArray(rows) ? rows : []).filter((row) => {
    const id = __asStr(row?.id);
    if (!id || exclude.has(id)) return false;
    if (row?.is_inactive) return false;
    if (isTrainerMirrorPersonRow(row)) return false;
    return clubPersonNameKey(row?.first_name, row?.last_name) === key;
  });
}

/**
 * @param {import('@supabase/supabase-js').SupabaseClient} supabase
 * @param {string} tenantId
 * @param {unknown} firstName
 * @param {unknown} lastName
 * @param {{ excludeIds?: string[] }} [options]
 */
export async function findClubPeopleByNormalizedNameInTenant(
  supabase,
  tenantId,
  firstName,
  lastName,
  options = {},
) {
  const tid = __asStr(tenantId);
  const fn = normalizeClubPersonNamePart(firstName);
  const ln = normalizeClubPersonNamePart(lastName);
  if (!supabase || !tid || !fn || !ln) return [];

  const { data, error } = await supabase
    .from("club_people")
    .select(
      "id,first_name,last_name,email,contact_source,birth_year,birth_date,tenant_id,is_inactive",
    )
    .eq("tenant_id", tid)
    .eq("is_inactive", false)
    .ilike("first_name", fn)
    .ilike("last_name", ln);

  if (error) return [];
  return filterClubPeopleByNormalizedName(data, firstName, lastName, options);
}

/**
 * @param {{
 *   tenant_id?: unknown,
 *   first_name?: unknown,
 *   last_name?: unknown,
 *   id?: unknown,
 *   contact_source?: unknown,
 * }} person
 * @param {{ excludeIds?: string[] }} [options]
 */
export async function assertNoDuplicateClubPersonName(person, options = {}) {
  if (isTrainerMirrorPersonRow(person)) return { ok: true };

  const tenantId = __asStr(person?.tenant_id);
  const firstName = person?.first_name;
  const lastName = person?.last_name;
  if (!tenantId || !clubPersonNameKey(firstName, lastName)) return { ok: true };

  const supabase = await getSupabaseClient();
  if (!supabase) return { ok: false, error: "no-supabase" };

  const excludeIds = [
    ...(Array.isArray(options.excludeIds) ? options.excludeIds : []),
    __asStr(person?.id),
  ].filter(Boolean);

  const matches = await findClubPeopleByNormalizedNameInTenant(
    supabase,
    tenantId,
    firstName,
    lastName,
    { excludeIds },
  );

  if (!matches.length) return { ok: true };
  return {
    ok: false,
    reason: "duplicate-name",
    error: "duplicate-name",
    matches,
  };
}
